import { useQuery } from '@tanstack/react-query';
import { GitCommitHorizontal } from 'lucide-react';
import { type ReactNode, createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { type GitProgressReviewWarning } from '@apm/contracts';
import { cn } from '../../../lib/utils';
import { progressCommitsApi, progressCommitsKeys } from '../progress-commits-api';
import { useGitPermissions } from '../use-git-permissions';

/** id việc → cảnh báo. `null` = không có bản đồ (chưa tải, không quyền, ngoài bảng). */
const WarningsContext = createContext<ReadonlyMap<string, GitProgressReviewWarning> | null>(null);

/**
 * Cảnh báo "AI rà commit thấy ít / không liên quan" cho cả bảng — APM-PCRV,
 * docs/plan-progress-commit-review.md §8. Một lời gọi, thẻ đọc qua context (cùng
 * khuôn `GitTaskMarksProvider`). Chỉ người có `git:read` mới hỏi.
 *
 * ⚠️ Không nhận asOf: đây là hàng đợi "PM chưa xem" **hiện hành** — như hộp thư, không
 * phải đại lượng theo thời gian. Lịch sử từng lượt rà nằm ở event của việc.
 */
export function ProgressReviewWarningsProvider({
  projectId,
  children,
}: {
  readonly projectId: string;
  readonly children: ReactNode;
}): ReactNode {
  const canRead = useGitPermissions(projectId).read;
  const warnings = useQuery({
    queryKey: progressCommitsKeys.warnings(projectId),
    queryFn: () => progressCommitsApi.warnings(projectId),
    enabled: canRead,
    // Dấu phụ trên thẻ: lỗi thì không có dấu, không toast.
    retry: false,
    staleTime: 60_000,
  });
  const list = canRead ? warnings.data?.warnings : undefined;
  return (
    <WarningsContext.Provider
      value={list ? new Map(list.map((item) => [item.taskId, item])) : null}
    >
      {children}
    </WarningsContext.Provider>
  );
}

/** Nhãn nhỏ trên thẻ — vàng (một phần) / đỏ (không liên quan). Việc không bị đổi gì. */
export function ProgressReviewBadge({ taskId }: { readonly taskId: string }): ReactNode {
  const { t } = useTranslation();
  const warning = useContext(WarningsContext)?.get(taskId);
  if (!warning) return null;
  const label = t(`progressCommits.review.verdict.${warning.verdict}`);
  const title = [t('progressCommits.review.badge.title', { verdict: label }), warning.summary]
    .filter((part): part is string => Boolean(part))
    .join(' — ');
  return (
    <span
      data-testid="progress-review-badge"
      title={title}
      className={cn(
        'inline-flex h-5 items-center gap-0.5 rounded-full border px-1.5 text-[11px]',
        warning.verdict === 'unrelated'
          ? 'border-rag-red/40 bg-rag-red/10 text-rag-red'
          : 'border-rag-amber/40 bg-rag-amber/10 text-rag-amber',
      )}
    >
      <GitCommitHorizontal aria-hidden className="size-3" />
      {t('progressCommits.review.badge.short')}
      {warning.count > 1 ? <span className="tabular-nums">×{warning.count}</span> : null}
      <span className="sr-only">{title}</span>
    </span>
  );
}
